import { useRef, useState } from 'react';
import PropTypes from 'prop-types';

import {
  buildPublicAttachmentPath,
  fetchCustomerAttachment,
  fetchCustomerRepairRequest,
  fetchCustomerSerialTrace,
  submitCustomerRepairRequest,
} from '#/api/customerPortal/client';
import { Button } from '#/modules/commonComponents/button';
import {
  describeRecordSource,
  describeRepairStage,
  describeWarrantyAvailability,
  getCustomerPortalErrorMessage,
} from '#/modules/customerPortal/helper/labels';

function emptyLine() {
  return { serialNumber: '', problemDescription: '', accessories: '' };
}

async function openAttachment(path, queryCode) {
  const response = await fetchCustomerAttachment(path, queryCode);
  const url = URL.createObjectURL(response.data);
  window.open(url, '_blank', 'noopener,noreferrer');
}

function AttachmentLinks({
  scope,
  serialNumber,
  repairRequestNo,
  repairItemId,
  reportId,
  attachments,
  queryCode,
  onError,
}) {
  const files = attachments ?? [];
  if (!files.length || !reportId) {
    return null;
  }
  return (
    <ul className="m-0 mt-2 list-none p-0">
      {files.map((file) => {
        const preview = buildPublicAttachmentPath({
          scope,
          serialNumber,
          repairRequestNo,
          repairItemId,
          reportId,
          fileId: file.fileId,
          version: file.version,
          action: 'preview',
        });
        const download = buildPublicAttachmentPath({
          scope,
          serialNumber,
          repairRequestNo,
          repairItemId,
          reportId,
          fileId: file.fileId,
          version: file.version,
          action: 'download',
        });
        if (!preview) {
          return null;
        }
        return (
          <li key={`${file.fileId}-${file.version}`} className="text-sm">
            <button
              type="button"
              className="text-primary-700 underline"
              onClick={() => openAttachment(preview, queryCode).catch(onError)}
            >
              預覽
              {' '}
              {file.originalFilename || file.fileId}
            </button>
            {download ? (
              <button
                type="button"
                className="ml-3 text-primary-700 underline"
                onClick={() => openAttachment(download, queryCode).catch(onError)}
              >
                下載
              </button>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

AttachmentLinks.propTypes = {
  scope: PropTypes.string.isRequired,
  serialNumber: PropTypes.string,
  repairRequestNo: PropTypes.string,
  repairItemId: PropTypes.string,
  reportId: PropTypes.string,
  attachments: PropTypes.arrayOf(PropTypes.object),
  queryCode: PropTypes.string,
  onError: PropTypes.func.isRequired,
};

const CustomerPortalPage = () => {
  const [serialNumber, setSerialNumber] = useState('');
  const [trace, setTrace] = useState(null);
  const [traceError, setTraceError] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [contactName, setContactName] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [lines, setLines] = useState([emptyLine()]);
  const [submitResult, setSubmitResult] = useState(null);
  const [submitError, setSubmitError] = useState('');
  const [repairRequestNo, setRepairRequestNo] = useState('');
  const [queryCode, setQueryCode] = useState('');
  const [requestDetail, setRequestDetail] = useState(null);
  const [requestError, setRequestError] = useState('');
  const attemptRef = useRef({ key: '', body: '' });

  async function showFileError(error) {
    let nextError = error;
    const body = error?.response?.data;
    if (body instanceof Blob) {
      try {
        const parsed = JSON.parse(await body.text());
        nextError = { response: { data: parsed } };
      } catch {
        nextError = error;
      }
    }
    setRequestError(getCustomerPortalErrorMessage(
      nextError,
      '附件暫時無法開啟，請稍後再試',
    ));
  }

  async function handleTrace(event) {
    event.preventDefault();
    setTraceError('');
    setTrace(null);
    try {
      setTrace(await fetchCustomerSerialTrace(serialNumber));
    } catch (error) {
      setTraceError(getCustomerPortalErrorMessage(error, '查不到此序號'));
    }
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setSubmitError('');
    const payload = {
      customerName: customerName || null,
      contactName: contactName || null,
      phone: phone || null,
      address: address || null,
      lines: lines.map((line) => ({
        serialNumber: line.serialNumber,
        problemDescription: line.problemDescription,
        accessories: line.accessories || null,
      })),
    };
    const body = JSON.stringify(payload);
    if (attemptRef.current.body !== body) {
      attemptRef.current = { key: crypto.randomUUID(), body };
    }
    try {
      const result = await submitCustomerRepairRequest(payload, attemptRef.current.key);
      setSubmitResult(result);
      setRepairRequestNo(result?.repairRequestNo || '');
      setQueryCode(result?.queryCode || '');
    } catch (error) {
      setSubmitError(getCustomerPortalErrorMessage(error, '送件失敗'));
    }
  }

  async function handleQuery(event) {
    event.preventDefault();
    setRequestError('');
    setRequestDetail(null);
    try {
      setRequestDetail(await fetchCustomerRepairRequest(repairRequestNo, queryCode));
    } catch (error) {
      setRequestError(getCustomerPortalErrorMessage(error, '找不到報修單'));
    }
  }

  const warranty = trace?.salesWarranty;

  return (
    <main className="mx-auto w-full max-w-3xl space-y-6 p-6">
      <h1 className="text-2xl font-medium text-gray-900">產品履歷</h1>

      <section className="rounded border border-slate-200 p-4">
        <h2 className="text-base font-medium text-gray-900">序號查詢</h2>
        <form className="mt-3 flex flex-wrap items-end gap-2" onSubmit={handleTrace}>
          <label className="min-w-0 flex-1 text-sm text-gray-700">
            完整序號
            <input
              value={serialNumber}
              onChange={(event) => setSerialNumber(event.target.value)}
              className="mt-1 h-10 w-full rounded border border-gray-300 px-3"
            />
          </label>
          <Button type="submit">查詢</Button>
        </form>
        {traceError ? <p className="mt-2 text-sm text-destructive">{traceError}</p> : null}
        {trace ? (
          <div className="mt-3 text-sm text-gray-700">
            <p>
              {trace.productName || '—'}
              {' · '}
              {describeRecordSource(trace.recordSource)}
            </p>
            <p className="mt-1">{describeWarrantyAvailability(warranty?.availability)}</p>
            {warranty?.availability === 'AVAILABLE' ? (
              <p className="mt-1">
                銷售日
                {' '}
                {warranty.salesDate || '—'}
                {' · 保固迄 '}
                {warranty.warrantyExpiryDate || '—'}
              </p>
            ) : null}
            {(trace.inspectionReports ?? []).map((report) => (
              <div key={report.reportId} className="mt-3">
                <p>
                  {report.inspectionKindName || report.inspectionKindCode}
                  {' · '}
                  {report.overallResult}
                </p>
                <AttachmentLinks
                  scope="inspection"
                  serialNumber={trace.serialNumber || serialNumber}
                  reportId={report.reportId}
                  attachments={report.attachments}
                  onError={showFileError}
                />
              </div>
            ))}
            {(trace.legacyInspectionReports ?? []).map((report, index) => (
              <p key={`${report.reportFamily}-${index}`} className="mt-2">
                {report.reportFamily}
                {' · '}
                {report.overallResult}
                {' · '}
                {report.sourceTimeText || '時間未記錄'}
              </p>
            ))}
            {(trace.repairHistory ?? []).map((item) => (
              <div key={item.repairItemId} className="mt-3">
                <p>
                  {item.repairRequestNo}
                  {' · '}
                  {describeRepairStage(item.stage, item.outcome)}
                </p>
                <p>{item.problemDescription}</p>
                <AttachmentLinks
                  scope="repair"
                  serialNumber={trace.serialNumber || serialNumber}
                  reportId={item.repairReport?.reportId}
                  attachments={item.repairReport?.attachments}
                  onError={showFileError}
                />
              </div>
            ))}
          </div>
        ) : null}
      </section>

      <section className="rounded border border-slate-200 p-4">
        <h2 className="text-base font-medium text-gray-900">匿名報修</h2>
        <form className="mt-3 space-y-3" onSubmit={handleSubmit}>
          <label className="block text-sm text-gray-700">
            客戶名稱（可空白）
            <input
              value={customerName}
              onChange={(event) => setCustomerName(event.target.value)}
              className="mt-1 h-10 w-full rounded border border-gray-300 px-3"
            />
          </label>
          <label className="block text-sm text-gray-700">
            聯絡人（可空白）
            <input
              value={contactName}
              onChange={(event) => setContactName(event.target.value)}
              className="mt-1 h-10 w-full rounded border border-gray-300 px-3"
            />
          </label>
          <label className="block text-sm text-gray-700">
            電話（可空白）
            <input
              value={phone}
              onChange={(event) => setPhone(event.target.value)}
              className="mt-1 h-10 w-full rounded border border-gray-300 px-3"
            />
          </label>
          <label className="block text-sm text-gray-700">
            地址（可空白）
            <input
              value={address}
              onChange={(event) => setAddress(event.target.value)}
              className="mt-1 h-10 w-full rounded border border-gray-300 px-3"
            />
          </label>
          {lines.map((line, index) => (
            <div key={index} className="rounded border border-slate-200 p-3">
              <label className="block text-sm text-gray-700">
                序號
                <input
                  value={line.serialNumber}
                  onChange={(event) => setLines((prev) => prev.map((item, itemIndex) => (
                    itemIndex === index
                      ? { ...item, serialNumber: event.target.value }
                      : item
                  )))}
                  className="mt-1 h-10 w-full rounded border border-gray-300 px-3"
                />
              </label>
              <label className="mt-2 block text-sm text-gray-700">
                問題描述
                <textarea
                  value={line.problemDescription}
                  onChange={(event) => setLines((prev) => prev.map((item, itemIndex) => (
                    itemIndex === index
                      ? { ...item, problemDescription: event.target.value }
                      : item
                  )))}
                  className="mt-1 w-full rounded border border-gray-300 px-3 py-2"
                  rows={2}
                />
              </label>
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            onClick={() => setLines((prev) => [...prev, emptyLine()])}
          >
            加一件
          </Button>
          <Button type="submit">送出報修</Button>
        </form>
        {submitError ? <p className="mt-2 text-sm text-destructive">{submitError}</p> : null}
        {submitResult ? (
          <p className="mt-3 text-sm text-gray-800">
            報修單號
            {' '}
            {submitResult.repairRequestNo}
            。查詢碼請自行保存，此頁重新整理後不會留下。
          </p>
        ) : null}
      </section>

      <section className="rounded border border-slate-200 p-4">
        <h2 className="text-base font-medium text-gray-900">查詢進度</h2>
        <form className="mt-3 space-y-3" onSubmit={handleQuery}>
          <label className="block text-sm text-gray-700">
            報修單號
            <input
              value={repairRequestNo}
              onChange={(event) => setRepairRequestNo(event.target.value)}
              className="mt-1 h-10 w-full rounded border border-gray-300 px-3"
            />
          </label>
          <label className="block text-sm text-gray-700">
            查詢碼
            <input
              value={queryCode}
              onChange={(event) => setQueryCode(event.target.value)}
              autoComplete="off"
              className="mt-1 h-10 w-full rounded border border-gray-300 px-3"
            />
          </label>
          <Button type="submit">查詢進度</Button>
        </form>
        {requestError ? <p className="mt-2 text-sm text-destructive">{requestError}</p> : null}
        {(requestDetail?.items ?? []).map((item) => (
          <div key={item.repairItemId} className="mt-3 text-sm text-gray-700">
            <p>
              {item.serialNumber}
              {' · '}
              {describeRepairStage(item.stage, item.outcome)}
            </p>
            <p>{item.problemDescription}</p>
            <p className="text-gray-500">{`配件：${item.accessories || '無'}`}</p>
            <AttachmentLinks
              scope="requestItem"
              repairRequestNo={requestDetail.repairRequestNo}
              repairItemId={item.repairItemId}
              reportId={item.repairReport?.reportId}
              attachments={item.repairReport?.attachments}
              queryCode={queryCode}
              onError={showFileError}
            />
          </div>
        ))}
      </section>
    </main>
  );
};

export default CustomerPortalPage;
